import isEmpty from "lodash-es/isEmpty";
import type { FocusEvent } from "react";
import {
  type Control,
  type FieldPath,
  type FieldValues,
  useController,
} from "react-hook-form";
import { Input } from "@/components/atoms/input";
import {
  FieldViewWrapper,
  type FieldViewWrapperProperties,
} from "@/components/molecules/form/FieldViewWrapper";

export interface TextFieldProps<
  TFieldValues extends FieldValues = Record<string, unknown>,
> extends Omit<FieldViewWrapperProperties, "children"> {
  control: Control<TFieldValues>;
  name: string;
  defaultValue?: string;
  modal?: boolean;
  filled?: boolean;
  required?: boolean;
  /** Called on blur with the current input value (e.g. for editor Redux sync). */
  onBlurWithValue?: (value: string) => void;
  disabled?: boolean;
}

export const TextFieldView = <
  TFieldValues extends FieldValues = Record<string, unknown>,
>({
  label,
  id,
  control,
  defaultValue = "",
  style,
  name,
  toHide = false,
  required,
  errorMessage,
  className,
  labelClassName,
  onBlurWithValue,
  disabled = false,
}: TextFieldProps<TFieldValues>) => {
  const { field } = useController({
    name: name as FieldPath<TFieldValues>,
    control,
    defaultValue: defaultValue as Parameters<
      typeof useController<TFieldValues>
    >[0]["defaultValue"],
    rules: { required: required },
  });
  const hasError = !isEmpty(errorMessage);

  return (
    <FieldViewWrapper
      id={id}
      label={label}
      errorMessage={errorMessage}
      toHide={toHide}
      className={className}
      style={style}
      labelClassName={labelClassName}
    >
      <Input
        id={id}
        disabled={disabled}
        onChange={(e) => field.onChange(e.target.value)}
        onBlur={(e: FocusEvent<HTMLInputElement>) => {
          onBlurWithValue?.(e.target.value);
          field.onBlur();
        }}
        value={typeof field.value === "string" ? field.value : ""}
        ref={field.ref}
        className={"mt-1 shadow-sm"}
        aria-invalid={hasError}
        aria-describedby={hasError ? `${id}-error` : undefined}
      />
    </FieldViewWrapper>
  );
};
